import { type ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { type OfferPortalWizardEvent } from 'state_machines/OfferPortalWizardMachine';

import { type AGENT_TYPES } from './offerDraft';

export type Send = (event: OfferPortalWizardEvent) => void;

// Literal classes so the generated atoms CSS always carries them; a computed
// arbitrary width would only exist after a dev-server restart.
const PROGRESS_WIDTHS = ['w-1/6', 'w-1/3', 'w-1/2', 'w-2/3', 'w-5/6', 'w-full'] as const;

/**
 * How far through the wizard the agent is, as the design's bar under the site
 * header. `step` is 1-based; the bar is decorative, so the step count stays in
 * the page header for assistive tech.
 */
export const WizardProgress = ({ step, of }: { step: number; of: number }) => {
  const filled = PROGRESS_WIDTHS.length - Math.max(of - step, 0) - 1;
  return (
    <Box aria-hidden className="h-1 w-full bg-od-bg-secondary">
      <Box
        className={cn(
          'h-full bg-od-bg-accent-brand transition-[width] duration-300',
          PROGRESS_WIDTHS[Math.min(Math.max(filled, 0), PROGRESS_WIDTHS.length - 1)],
        )}
      />
    </Box>
  );
};

/**
 * Shared by both wizard shells so the heading cannot drift. Below `md` the
 * back control sits above the title. From `md` it hangs 36px left so the
 * heading lines up with the field cards.
 */
export const WizardHeader = ({
  step,
  of,
  title,
  back,
}: {
  step: number;
  of: number;
  title: string;
  back: ReactNode;
}) => (
  <Col as="header" className="w-full gap-3 md:relative md:gap-0">
    <Box className="md:absolute md:top-1/2 md:right-full md:-translate-y-1/2 md:pr-9">{back}</Box>
    <Row className="w-full items-center">
      <Text
        as="h1"
        className="min-w-0 flex-1 font-medium text-[32px] tracking-[-0.896px] text-od-content-primary"
      >
        {title}
      </Text>
      <Text className="shrink-0 whitespace-nowrap text-body-sm text-od-content-secondary">
        Step {step} of {of}
      </Text>
    </Row>
  </Col>
);

/** Symmetric gutters on mobile. From `md`, left padding fits the hanging back. */
export const WIZARD_PAGE_GUTTER =
  'w-full justify-center px-6 pt-10 md:pl-[72px] lg:px-10 lg:pl-[72px]';

/** Stacked steps share the form column. Two columns use the full 784px. */
export const WIZARD_PAGE_WIDTH = 'w-full max-w-[493px] gap-6 lg:max-w-[784px]';

export const WIZARD_COLUMNS = 'w-full flex-col gap-6 lg:flex-row lg:items-start';

export const WIZARD_FORM_COL = 'min-w-0 w-full max-w-[493px] flex-1 gap-6';

export const FIELD_CARD =
  'w-full min-w-0 flex-col gap-4 rounded-[16px] border border-od-border-primary p-4';

/**
 * Text and currency inputs across the wizard. The atoms default is 64px tall,
 * which towers over the 42px radio rows and the date picker beside it, so the
 * fields take the compact size and the design's 43px height.
 */
export const FIELD_INPUT = 'h-[43px] w-full';

/** The field label, turned to the error color while its value is invalid. */
export const fieldLabel = (invalid: boolean): string =>
  cn(
    'font-medium text-[16px] tracking-[-0.3px]',
    invalid ? 'text-od-content-error' : 'text-od-content-primary',
  );

/** A card's question title — one size up from the input labels, per design. */
export const cardTitle = (invalid = false): string =>
  cn(
    'font-medium text-[18px] tracking-[-0.432px]',
    invalid ? 'text-od-content-error' : 'text-od-content-primary',
  );

// The `**:data-checked` variants recolor the atoms radio control (dark by
// default) to the brand blue this design selects with; `!` because
// tailwind-merge cannot arbitrate token-color classes.
export const RADIO_ROW =
  'h-[42px] w-full flex-row-reverse items-center justify-between rounded-[12px] bg-od-bg-secondary px-4 border border-transparent transition-colors has-data-checked:border-od-border-accent-brand **:data-checked:!bg-od-bg-accent-brand **:data-checked:!ring-od-border-accent-brand';

// A radio group renders its own label, so its label-to-options distance comes
// from the group, not the card. These line it up with the rest of the wizard:
// a card-heading group matches the card's title gap, a sub-field group matches
// the gap a labelled input uses.
export const RADIO_GROUP_AS_CARD_TITLE = 'gap-4';
export const RADIO_GROUP_AS_FIELD = 'gap-2';

/** Whether the submitting agent belongs to the MLS, as legacy asked it. */
export const AGENT_TYPE_OPTIONS = [
  { label: 'MLS', value: 'mls' },
  { label: 'Non-MLS', value: 'non_mls' },
] as const satisfies readonly { label: string; value: (typeof AGENT_TYPES)[number] }[];

/** The legacy financing_type choices, as the design lists them. */
export const FINANCING_OPTIONS = [
  { label: 'Cash', value: 'cash' },
  { label: 'Conventional', value: 'conventional' },
  { label: 'FHA', value: 'fha' },
  { label: 'VA', value: 'va' },
  { label: 'Other', value: 'other' },
] as const;

/** The legacy contingent_offer_status choices, labeled as the design shows. */
export const CONTINGENT_STATUS_OPTIONS = [
  { label: 'Listed', value: 'listed' },
  { label: 'Not Listed', value: 'unlisted' },
  { label: 'In contract', value: 'in_contract' },
] as const;

/** The intake lender choices for loan financing. */
export const LENDER_OPTIONS = [
  { label: 'Opendoor Home Loans', value: 'opendoor_home_loans' },
  { label: 'External Lender', value: 'non_partner' },
] as const;

/** The intake contingency periods, one number input each on the details step. */
export const PERIOD_FIELDS = [
  {
    field: 'inspectionPeriodDays',
    contextKey: 'inspectionPeriodDays',
    event: 'SET_INSPECTION_PERIOD_DAYS',
    id: 'inspection-period',
    label: 'Inspection period (days)',
  },
  {
    field: 'appraisalPeriodDays',
    contextKey: 'appraisalPeriodDays',
    event: 'SET_APPRAISAL_PERIOD_DAYS',
    id: 'appraisal-period',
    label: 'Appraisal period (days)',
  },
  {
    field: 'financingPeriodDays',
    contextKey: 'financingPeriodDays',
    event: 'SET_FINANCING_PERIOD_DAYS',
    id: 'financing-period',
    label: 'Financing period (days)',
  },
] as const;

/**
 * A card's heading and its supporting line. One component so every card keeps
 * the same tight pairing: the caption belongs to the title, and the card's own
 * gap separates the pair from the controls below.
 */
export const CardHeading = ({
  title,
  caption,
  invalid = false,
  htmlFor,
}: {
  title: ReactNode;
  caption?: ReactNode;
  invalid?: boolean;
  /** Set when the heading is the label for the card's single control. */
  htmlFor?: string;
}) => (
  <Col className="gap-1">
    {htmlFor != null ? (
      <Text as="label" htmlFor={htmlFor} className={cardTitle(invalid)}>
        {title}
      </Text>
    ) : (
      <Text as="h2" className={cardTitle(invalid)}>
        {title}
      </Text>
    )}
    {caption != null ? (
      <Text className="text-body-sm text-od-content-secondary">{caption}</Text>
    ) : null}
  </Col>
);

export const FieldError = ({ children }: { children: ReactNode }) => (
  <Text role="alert" className="text-body-sm text-od-content-error">
    {children}
  </Text>
);

/**
 * The uploaded-file tile, shared by the documents step and the review.
 * `w-full min-w-0` keeps a long file name from setting the row's minimum
 * width and stretching the card past its column.
 */
export const DOCUMENT_ROW =
  'w-full min-w-0 items-center gap-2 rounded-[12px] bg-od-bg-secondary p-4';

export const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
};
